import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { Icon } from './Icon.tsx';
import { useT } from '~/i18n/index.tsx';

/**
 * The page's heading: its name, and its actions at the far end.
 *
 * Set straight on the page rather than in a card. The reference boxed it with an accent emblem
 * tile repeating the rail's glyph — the rail already says where you are, and a page that names
 * itself twice before the work starts is a template, not a design. For the same reason there is
 * no line under the name explaining the page: staff use these screens every day, and a sentence
 * read once and skipped forever after only pushes the work down the screen.
 */
export function PageTitle({
  title,
  count,
  back,
  actions,
}: {
  title: string;
  /** How many records stand behind the title. Formatted by the caller, which is where the locale
      lives. */
  count?: string;
  /** Where the page above this one lives. Renders the same back pill the form pages carry. */
  back?: string;
  actions?: ReactNode;
}) {
  const t = useT();

  return (
    <header className="page-header">
      <div className="page-header__bar">
        <div className="page-header__identity">
          {back ? (
            <Link className="formpage__back" to={back}>
              <Icon name="back" className="rtl-flip" size={18} strokeWidth={2} />
              <span className="formpage__back-label">{t('common.back')}</span>
            </Link>
          ) : null}

          <div className="page-header__titles">
            <h1 className="page-header__title">
              <bdi>{title}</bdi>
            </h1>

            {count ? (
              <p className="page-header__meta">
                <span className="page-header__count">{count}</span>
              </p>
            ) : null}
          </div>
        </div>

        {actions ? <div className="page-header__actions">{actions}</div> : null}
      </div>
    </header>
  );
}
